import React, { useState, useMemo } from "react";
import { Typography, TextField } from "@mhome/ui";

const NumberCell = ({
  value,
  onChange,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  meta = {},
  ...cellProps
}) => {
  const [editValue, setEditValue] = useState(value || "0");

  const { min, max, step = 1 } = meta;

  const handleCellClick = () => {
    if (!isEditing) {
      setEditValue(value || "0");
      onEdit();
    }
  };

  const handleSave = () => {
    // Validate number
    const numValue = parseFloat(editValue);
    if (isNaN(numValue)) {
      // Invalid number, revert to original value
      setEditValue(value || "0");
      onCancel();
      return;
    }

    // Apply min/max constraints if specified
    let finalValue = numValue;
    if (min !== undefined && finalValue < min) {
      finalValue = min;
    }
    if (max !== undefined && finalValue > max) {
      finalValue = max;
    }

    const finalValueStr = finalValue.toString();
    setEditValue(finalValueStr);
    onChange(finalValueStr);
    onSave();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  const handleChange = (e) => {
    setEditValue(e.target.value);
  };

  // Parse the display value
  const displayValue = value || "0";
  const parsedValue = parseFloat(displayValue);
  const formattedDisplay = isNaN(parsedValue)
    ? displayValue
    : parsedValue.toString();

  // Convert MUI spacing to pixels (MUI spacing unit = 8px)
  const spacingToPx = (spacing) =>
    typeof spacing === "number" ? spacing * 8 : spacing;

  const cellStyle = useMemo(
    () => ({
      border: isEditing ? `2px solid hsl(var(--primary))` : "none",
      borderRadius: isEditing ? "8px" : 0,
    }),
    [isEditing]
  );

  return (
    <div
      className="cursor-pointer p-1 w-full h-10 flex items-center"
      style={cellStyle}
      onClick={handleCellClick}
      onMouseEnter={(e) => {
        if (!isEditing) {
          e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      {isEditing ? (
        <TextField
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          size="small"
          autoFocus
          type="number"
          inputProps={{
            min,
            max,
            step,
          }}
          style={{ width: "100%" }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "32px",
            },
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <Typography
          variant="body2"
          className="px-2 w-full overflow-hidden text-ellipsis"
        >
          {formattedDisplay}
        </Typography>
      )}
    </div>
  );
};

export default NumberCell;
